import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FileText, Send, AlertTriangle } from 'lucide-react';

interface SolicitarFaturaModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Razão social do cliente (mostrado no diálogo) */
  razaoSocial: string;
  /** Linha da conta de fatura (11 dígitos). Sem isso, o Bee não consegue buscar. */
  linhaContaFatura?: string | null;
  /** Disparado quando o consultor confirma. Quem chamou faz o trabalho real (useSolicitarFatura). */
  onSolicitarAutomatico?: () => void;
  /** ID do consultor dono do lead — usado pra bloquear quem não é dono */
  consultorId?: string | null;
  /** Nome do consultor dono (mostrado no aviso) */
  consultorNome?: string | null;
  /** ID do usuário logado — se diferente de consultorId, bloqueia */
  currentUserId?: string | null;
}

/**
 * Modal de confirmação pra solicitar fatura via Bee360 (N8N).
 *
 * Antes tinha 2 modos (automático + manual). O modo manual gerava ticket
 * em `fila_fatura_manual`, que foi removida — toda fatura agora é via Bee.
 * Modal ficou só pra confirmar antes de disparar (e mostrar bloqueio de
 * não-dono ou linha vazia).
 */
export function SolicitarFaturaModal({
  open,
  onOpenChange,
  razaoSocial,
  linhaContaFatura,
  onSolicitarAutomatico,
  consultorId,
  consultorNome,
  currentUserId,
}: SolicitarFaturaModalProps) {
  const isOwner = !currentUserId || !consultorId || consultorId === currentUserId;
  const temLinha = linhaContaFatura && linhaContaFatura.replace(/\D/g, '').length === 11;
  const podeSolicitar = isOwner && temLinha;

  const handleSolicitar = () => {
    onSolicitarAutomatico?.();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Solicitar Fatura
          </DialogTitle>
          <DialogDescription>
            Solicitar fatura via Bee360 para <strong>{razaoSocial}</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {!isOwner && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Você não pode solicitar fatura para um lead que não é seu.
                {consultorNome && (
                  <> Solicite ao consultor responsável <strong>{consultorNome}</strong> que faça essa solicitação.</>
                )}
              </AlertDescription>
            </Alert>
          )}

          {!temLinha && isOwner && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Linha da conta não preenchida. Preencha a linha (11 dígitos com DDD) antes de solicitar.
              </AlertDescription>
            </Alert>
          )}

          {podeSolicitar && (
            <div className="flex items-start gap-3 border border-primary/20 bg-primary/5 rounded-lg p-3">
              <Send className="h-4 w-4 mt-0.5 text-primary" />
              <div className="text-sm">
                <div className="font-medium">Busca automática via Bee360</div>
                <p className="text-muted-foreground mt-1">
                  Vamos enviar pra integração e o status aparece aqui assim que o robô responder.
                  Pode levar alguns minutos em horário comercial (6h-21h dias úteis).
                </p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSolicitar} disabled={!podeSolicitar}>
            Solicitar Fatura
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
